import React from "react";

// =====================================================================
// Last Minute V2 rebuild — verbatim artboard glyph kit (brief RULE 2).
//
// The LastMinute handoff draws its card symbols as CSS-span constructions,
// not SVGs. Each glyph below copies the artboard's span geometry exactly
// (sizes, borders, colors per surface) so no merely-similar library icon
// ever substitutes for an artboard mark. Sizing variants mirror the
// artboard's own desktop/mobile variants (e.g. share 14×15 → 15×17).
// =====================================================================

// Share tray + up-arrow (card footer square button). Desktop 14×15 (1a),
// mobile 15×17 (8a). Color #33475E.
export const ShareGlyph = ({ mobile = false }) => {
  const c = "#33475E";
  const w = mobile ? 15 : 14;
  const h = mobile ? 17 : 15;
  return (
    <span className="relative inline-block" style={{ width: w, height: h }} aria-hidden="true" data-testid="lm-share-glyph">
      <span
        className="absolute left-0 right-0 bottom-0 rounded-[2px]"
        style={{ height: mobile ? 9 : 8, border: `1.5px solid ${c}`, borderTop: "none" }}
      />
      <span
        className="absolute left-1/2 -translate-x-1/2"
        style={{ top: mobile ? 3 : 2.5, width: 1.5, height: mobile ? 8 : 7, background: c }}
      />
      <span
        className="absolute left-1/2"
        style={{
          top: 1,
          width: mobile ? 6.5 : 6,
          height: mobile ? 6.5 : 6,
          borderLeft: `1.5px solid ${c}`,
          borderTop: `1.5px solid ${c}`,
          transform: "translateX(-50%) rotate(45deg)",
        }}
      />
    </span>
  );
};

// Padlock (anonymized "Name revealed on interest" / chat "Visible only to …").
// Card 10×13 (#98A5B8); chat-card 9×11; mobile chat 8×10. `color` covers the
// chat card's blue reveal-CTA tint (#1466D8) — default stays the artboard grey.
export const LockGlyph = ({ size = "card", color = "#98A5B8" }) => {
  const v = {
    card: { w: 10, h: 13, sw: 6, sh: 6, sl: 2, sb: 1.5, bw: 10, bh: 7 },
    chat: { w: 9, h: 11, sw: 5, sh: 5, sl: 1.5, sb: 1.3, bw: 9, bh: 6 },
    chatMobile: { w: 8, h: 10, sw: 4, sh: 4, sl: 1.5, sb: 1.2, bw: 8, bh: 5 },
  }[size] || { w: 10, h: 13, sw: 6, sh: 6, sl: 2, sb: 1.5, bw: 10, bh: 7 };
  return (
    <span className="relative inline-block flex-none" style={{ width: v.w, height: v.h }} aria-hidden="true" data-testid="lm-lock-glyph">
      <span
        className="absolute top-0"
        style={{
          left: v.sl,
          width: v.sw,
          height: v.sh,
          border: `${v.sb}px solid ${color}`,
          borderBottom: "none",
          borderRadius: "3px 3px 0 0",
        }}
      />
      <span
        className="absolute bottom-0 left-0 rounded-[2px]"
        style={{ width: v.bw, height: v.bh, background: color }}
      />
    </span>
  );
};

// Serif-italic "i" ring (blocked-contact note, 14px — the page-title dot is
// ScoutInfoDot's own 18px ring, untouched).
// Defaults = the 1a blocked-note ring, byte-identical to the original. The
// LM-COMPOSE boards reuse the same glyph with their own ring (#C3CFDF label
// dots, 4b/5a) via `ring`, no top-nudge via `mt`, and the 7c OPEN state
// (solid #2283FF + soft ring) via `active`.
export const InfoIGlyph = ({ size = 14, ring = "#A7B4C6", mt = 1, active = false, className = "" }) => (
  <span
    className={`flex-none inline-flex items-center justify-center rounded-full font-bold ${className}`}
    style={{
      width: size,
      height: size,
      fontSize: Math.round(size * 0.64),
      fontFamily: "Georgia, serif",
      fontStyle: "italic",
      marginTop: mt,
      ...(active
        ? { background: "#2283FF", color: "#fff", boxShadow: "0 0 0 3px rgba(34,131,255,.18)" }
        : { border: `1.3px solid ${ring}`, color: "#8A98AC" }),
    }}
    aria-hidden="true"
  >
    i
  </span>
);

// CSS magnifier — search box (12×12, idle #93A5BC / focused #5A6B82) and the
// quick-view loupe (13×13, #1466D8, inside its 30px bubble).
export const MagnifierGlyph = ({ size = 12, color = "#93A5BC" }) => {
  const ring = Math.round(size * (size >= 13 ? 0.69 : 0.67));
  const stroke = size >= 13 ? 1.6 : 1.5;
  return (
    <span className="relative inline-block flex-none" style={{ width: size, height: size }} aria-hidden="true">
      <span
        className="absolute top-0 left-0 rounded-full"
        style={{ width: ring, height: ring, border: `${stroke}px solid ${color}` }}
      />
      <span
        className="absolute"
        style={{ bottom: 0, right: size >= 13 ? 0 : 1, width: 5, height: stroke, background: color, transform: "rotate(45deg)" }}
      />
    </span>
  );
};

// Pending-request clock (9c "Request pending" footer state): 12px ring + hand.
export const PendingClockGlyph = () => (
  <span className="relative inline-block flex-none" style={{ width: 12, height: 12 }} aria-hidden="true" data-testid="lm-pending-glyph">
    <span className="absolute inset-0 rounded-full" style={{ border: "1.5px solid #98A5B8" }} />
    <span className="absolute left-1/2 -translate-x-1/2" style={{ top: 2.5, width: 1.5, height: 3.5, background: "#98A5B8" }} />
  </span>
);

// Empty-state clock (1d): 22px ring + two hands inside the 52px #EEF3FA circle.
export const EmptyClockGlyph = () => (
  <span className="relative inline-block" style={{ width: 22, height: 22 }} aria-hidden="true">
    <span className="absolute inset-0 rounded-full" style={{ border: "2px solid #9DB4D0" }} />
    <span className="absolute left-1/2 -translate-x-1/2 rounded-[2px]" style={{ top: 5, width: 2, height: 7, background: "#9DB4D0" }} />
    <span className="absolute left-1/2 rounded-[2px]" style={{ top: 10, width: 6, height: 2, background: "#9DB4D0" }} />
  </span>
);

// Agency initials badge (card agent row): 16×16, radius 5, white 7.5px bold
// initials on a deep-tone tile (artboard shows NS/BS/V11/TS on distinct deep
// hues — deterministic pick so an agency keeps its color everywhere).
const BADGE_PALETTE = ["#1F3A5F", "#4A3A6B", "#145C43", "#8A4B12", "#33517A", "#5A2E4F"];
export const agencyInitials = (name = "") => {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  const caps = words.map((w) => (/^\d/.test(w) ? w.replace(/[^A-Za-z0-9]/g, "").slice(0, 3) : w[0].toUpperCase()));
  return caps.join("").slice(0, 3);
};
export const AgencyBadge = ({ agency, size = 16 }) => {
  const initials = agencyInitials(agency);
  if (!initials) return null;
  let sum = 0;
  for (let i = 0; i < String(agency).length; i += 1) sum += String(agency).charCodeAt(i);
  return (
    <span
      className="inline-flex items-center justify-center rounded-[5px] font-bold text-white flex-none"
      style={{ width: size, height: size, fontSize: 7.5, background: BADGE_PALETTE[sum % BADGE_PALETTE.length] }}
      aria-hidden="true"
      data-testid="lm-agency-badge"
    >
      {initials}
    </span>
  );
};

// Loading shimmer (1e): the artboard's tpShim sweep. Injected once; use
// className="lm-shim" on skeleton spans.
let shimInjected = false;
export const ensureLmShimmer = () => {
  if (shimInjected || typeof document === "undefined") return;
  if (document.getElementById("lm-shim-style")) {
    shimInjected = true;
    return;
  }
  const style = document.createElement("style");
  style.id = "lm-shim-style";
  style.textContent =
    "@keyframes tpShim{0%{background-position:120% 0}100%{background-position:-120% 0}}" +
    ".lm-shim{background:linear-gradient(90deg,#E9EEF4 30%,#F6F8FB 50%,#E9EEF4 70%);background-size:200% 100%;animation:tpShim 1.4s linear infinite}";
  document.head.appendChild(style);
  shimInjected = true;
};

// ── LM-COMPOSE additions (sections 4–6 boards) ───────────────────────

// Label/title serif-"i" dot (4b/5a boards): the shared InfoIGlyph in the
// #C3CFDF label-ring variant, no top nudge. One glyph, one export family.
export const SerifInfoGlyph = ({ size = 13, active = false, className = "" }) => (
  <InfoIGlyph size={size} ring="#C3CFDF" mt={0} active={active} className={className} />
);

// Empty-state plus (5c): two #9DB4D0 bars inside the caller's #EEF3FA circle.
export const PlusMarkGlyph = ({ size = 20 }) => (
  <span className="relative inline-block flex-none" style={{ width: size, height: size }} aria-hidden="true">
    <span
      className="absolute rounded-[2px] bg-[#9DB4D0]"
      style={{ left: size / 2 - 1, top: 0, width: 2, height: size }}
    />
    <span
      className="absolute rounded-[2px] bg-[#9DB4D0]"
      style={{ top: size / 2 - 1, left: 0, width: size, height: 2 }}
    />
  </span>
);

const lmGlyphs = {
  ShareGlyph,
  LockGlyph,
  InfoIGlyph,
  MagnifierGlyph,
  PendingClockGlyph,
  EmptyClockGlyph,
  AgencyBadge,
  agencyInitials,
  ensureLmShimmer,
  SerifInfoGlyph,
  PlusMarkGlyph,
};

export default lmGlyphs;
